/**
 * Logistics overhaul Phase 2a — single source of truth for freight
 * pipeline stage labels, variants, and operator-allowed transitions.
 *
 * Mirrors the backend constants in
 * `middleware/app/models/freight_pipeline.py` 1:1. Test
 * (`pipeline-stage-meta.test.ts`) pins the contract so future drift
 * between frontend + backend trips a unit test.
 */

import type { PipelineStage } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/**
 * Canonical ordered list — used by kanban column ordering + the
 * status-filter dropdown so both surfaces walk the pipeline in the
 * same direction.
 */
export const STAGES: readonly PipelineStage[] = [
  'quote_requested',
  'quoted',
  'tendered',
  'in_transit',
  'delivered',
  'exception',
  'cancelled',
] as const

// `exception` is deliberately NOT terminal — it's an active problem state
// (carrier delay, damage, refused delivery) ops must resolve back into the
// flow, not a dead end. Only `delivered` and `cancelled` are end-of-life.
export const TERMINAL_STAGES: readonly PipelineStage[] = [
  'delivered',
  'cancelled',
] as const

/**
 * Stages shown on the kanban board. Unlike sales-inbox, terminals stay on
 * the board here — inbound freight volume is low enough that archival
 * isn't needed to keep the board readable, and ops benefits from seeing
 * delivered/cancelled loads alongside active ones for a few days.
 */
export const BOARD_STAGES: readonly PipelineStage[] = STAGES

export const STAGE_LABELS: Record<PipelineStage, string> = {
  quote_requested: 'Quote Requested',
  quoted: 'Quoted',
  tendered: 'Tendered',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  exception: 'Exception',
  cancelled: 'Cancelled',
}

export const STAGE_SHORT_LABELS: Record<PipelineStage, string> = {
  quote_requested: 'Quote Requested',
  quoted: 'Quoted',
  tendered: 'Tendered',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  exception: 'Exception',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<PipelineStage, ChipVariant> = {
  quote_requested: 'neutral',
  quoted: 'info',
  tendered: 'info',
  in_transit: 'warning',
  delivered: 'success',
  exception: 'danger',
  cancelled: 'danger',
}

/**
 * Operator-driven transitions — mirrors the Python ALLOWED_TRANSITIONS
 * frozenset 1:1 (app/models/freight_pipeline.py):
 *   - Any non-terminal stage can move to any other stage (incl. either
 *     terminal).
 *   - Terminals can ONLY undo to `in_transit` — the recovery case is
 *     "marked delivered too early" or "cancelled by mistake while the
 *     load was moving," not an arbitrary jump back to quoting.
 *   - No cross-terminal moves (delivered <-> cancelled) — undo to
 *     in_transit first, then re-apply the other terminal.
 *
 * Derived programmatically rather than hand-listed so the table stays
 * in sync with `STAGES` + `TERMINAL_STAGES` if those ever change.
 */
export interface Transition {
  from: PipelineStage
  to: PipelineStage
}

const NON_TERMINAL_STAGES = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  ...NON_TERMINAL_STAGES.flatMap((from) =>
    STAGES.filter((to) => to !== from).map<Transition>((to) => ({ from, to })),
  ),
  { from: 'delivered', to: 'in_transit' },
  { from: 'cancelled', to: 'in_transit' },
] as const

export function isTerminal(stage: PipelineStage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}

export function transitionAllowed(from: PipelineStage, to: PipelineStage): boolean {
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: PipelineStage): PipelineStage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

/**
 * Manual (operator-typed) transitions available in the Phase 2a work view.
 * Excludes `tendered` — that stage is only ever reached through the
 * dedicated tender action (Phase 2b), which actually books a rate and
 * links a freight_shipment row. Allowing a bare stage-move into `tendered`
 * here would leave a pipeline card claiming a load is booked when nothing
 * was ever tendered.
 */
export function manualTransitionTargetsFrom(stage: PipelineStage): PipelineStage[] {
  return allowedTargetsFrom(stage).filter((s) => s !== 'tendered')
}
